import {
    clearOverlayCanvas,
    LineInProgress,
    strokeLineInProgress,
    syncCanvasSizeToLayout,
} from '~/engine/core/render/overlayCanvas';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { PlaneView } from '../../../view/views/PlaneView';

/**
 * Handles 2D canvas rendering for the line draw tool preview.
 * Draws the polyline preview, hover point, and snap-to-close
 * visual feedback on the PlaneView's 2D overlay canvas.
 */
export class LinePreviewRenderer {
    private readonly canvasContext: CanvasRenderingContext2D | null;
    private readonly planeView: PlaneView;
    private readonly roi: RegionOfInterest;

    public constructor(planeView: PlaneView, roi: RegionOfInterest) {
        this.planeView = planeView;
        this.roi = roi;

        const canvas = this.planeView.get2DCanvas;
        if (canvas) {
            this.canvasContext = canvas.getContext('2d');
            if (!this.canvasContext) {
                throw new Error('Canvas context unavailable');
            }
        } else {
            throw new Error('Canvas unavailable');
        }
    }

    private getRoiColor(): { R: number; G: number; B: number } {
        const color = this.roi.color;
        const R = Math.round(color[0] * 255);
        const G = Math.round(color[1] * 255);
        const B = Math.round(color[2] * 255);
        return { R, G, B };
    }

    /**
     * Renders the line preview on the 2D canvas overlay.
     * @param line The current line being drawn, or null.
     */
    public render(line: LineInProgress | null): void {
        const canvas = this.planeView.get2DCanvas;
        if (!canvas || !this.canvasContext) {
            console.error('Canvas or context is undefined.');
            return;
        }

        const ctx = this.canvasContext;
        syncCanvasSizeToLayout(ctx);
        clearOverlayCanvas(ctx);

        if (line) {
            const color = this.getRoiColor();
            strokeLineInProgress(
                ctx,
                this.planeView,
                line,
                `rgba(${color.R}, ${color.G}, ${color.B}, 1)`
            );
        }
    }

    /** Clears the 2D canvas overlay. */
    public clear(): void {
        if (this.canvasContext) {
            clearOverlayCanvas(this.canvasContext);
        }
    }

    /** Clears the canvas (alias for cleanup). */
    public dispose(): void {
        this.clear();
    }
}
